import { tenantRoute } from "../utils/tenantRoute";

export interface ValidatedTenantScope {
    /** Expected Access response identity from the admitted server selection. */
    accessProject?: string;
    urlRouteId?: string | null;
    scopeKey?: string;
    companyId: string;
    projectId: string;
    principalId: string;
    organizationId: string;
    /** The selected project's tenant slug (product-event and analytics organization). */
    storageOrgSlug?: string | null;
    /** The tab acts as Primepoint staff, not as a member (`actingAsStaff`): its requests carry
     *  the acting company. */
    actingAsStaff: boolean;
}
let binding: ValidatedTenantScope | null = null;
let selectedCompanyId: string | null = null;
let generation = 0;
let closed = false;
let required = typeof window !== "undefined" && !!tenantRoute().companySlug;
let scopeController: AbortController | null = null;
/** Native composite-signal support (Chrome 116+, Safari 17.4+, Firefox 124+,
 *  Node 20+); read lazily so a runtime ponyfill installed after module load
 *  still takes effect. TS 4.9's DOM lib predates the `any` static, and the
 *  global `AbortSignal` variable's type is anonymous (not a mergeable
 *  interface), so this narrows through a named, commented cast instead of an
 *  inline `any`-typed member read. */
function nativeAbortSignalAny(): ((signals: AbortSignal[]) => AbortSignal) | undefined {
    // Well-known platform constructor, not external data: `AbortSignal.any`
    // is a real static method on engines that implement it.
    const ctor = AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal };
    return ctor.any;
}
const bootstrapPaths = new Set([
    "/api/ai/session/me",
    "/api/ai/session/project-routes",
    "/api/ai/session/company-session",
    "/api/ai/session/oauth-continuation",
    "/api/ai/embed/refresh",
    "/api/ai/auth/discover",
    "/api/ai/auth/console-access",
]);

/** The browser admission owner and desktop host publish the same validated
 * content partition. Query/cache consumers observe it without owning admission
 * or remounting its provider during permission epoch changes. */
const scopeKeyListeners = new Set<() => void>();

export function subscribeTenantContentScopeKey(listener: () => void): () => void {
    scopeKeyListeners.add(listener);
    return () => {
        scopeKeyListeners.delete(listener);
    };
}

function notifyScopeKeyChanged(): void {
    for (const listener of scopeKeyListeners) listener();
}

export class TenantScopeUnavailableError extends Error {
    constructor(message = "Project access is being validated") {
        super(message);
        this.name = "TenantScopeUnavailableError";
    }
}
export function isTenantScopeRequired(): boolean {
    return required;
}
export function isTenantScopeValidated(): boolean {
    return binding !== null && !closed;
}
export function isTenantScopeDocumentClosed(): boolean {
    return closed;
}
export function captureTenantScopeGeneration(): number {
    return generation;
}
export function currentTenantContentScopeKey(): string | null {
    return closed ? null : (binding?.scopeKey ?? null);
}
export function currentTenantScope(): ValidatedTenantScope | null {
    return closed ? null : binding;
}
export function isSourceCredentialRequest(url?: string): boolean {
    try {
        return bootstrapPaths.has(new URL(url ?? "", window.location.origin).pathname);
    } catch {
        return false;
    }
}
export function isUnvalidatedTenantRequestAllowed(url?: string): boolean {
    if (isSourceCredentialRequest(url)) return true;
    try {
        return new URL(url ?? "", window.location.origin).pathname === "/api/ai/session/projects";
    } catch {
        return false;
    }
}
function clearScopeRequests(): void {
    generation++;
    const had = binding !== null;
    binding = null;
    scopeController?.abort();
    scopeController = null;
    if (had) notifyScopeKeyChanged();
}
export function resetTenantScope(): void {
    selectedCompanyId = null;
    required = true;
    clearScopeRequests();
}
/** A fresh company JWT admits this selector before a project is chosen. */
export function installSelectedCompany(companyId: string): void {
    selectedCompanyId = companyId;
}
/** Leaving canonical scope for a legacy URL mid-validation must drop the guard,
 *  or every ordinary legacy request throws TenantScopeUnavailableError. */
export function leaveTenantScope(): void {
    required = false;
    clearScopeRequests();
}
export function closeTenantScopeForDocument(): void {
    resetTenantScope();
    closed = true;
    notifyScopeKeyChanged();
}
export function installValidatedScope(next: ValidatedTenantScope): void {
    if (closed) throw new TenantScopeUnavailableError("This page's project session has ended");
    required = true;
    const changed = binding?.scopeKey !== next.scopeKey;
    binding = next;
    if (changed) notifyScopeKeyChanged();
}
export function assertTenantRequestScope(url: string | undefined, captured: number): void {
    if (!required) return;
    if (closed || captured !== generation)
        throw new TenantScopeUnavailableError("Project changed while the request was active");
    if (!binding && !isUnvalidatedTenantRequestAllowed(url))
        throw new TenantScopeUnavailableError();
}
export function getTenantRequestHeaders(): Record<string, string> {
    if (closed || (!binding && !selectedCompanyId)) return {};
    // A URL route's credential alone says whether its holder acts as a platform actor.
    if (binding?.urlRouteId) return { "X-Project-Route-ID": binding.urlRouteId };
    return {
        ...(selectedCompanyId ? { "X-Company-ID": selectedCompanyId } : {}),
        ...(binding ? { "X-Project-ID": binding.projectId } : {}),
        ...(binding?.actingAsStaff ? { "X-Acting-Company-ID": binding.companyId } : {}),
    };
}
/** Every header a caller must never set directly: the bearer, and every
 *  URL-admitted selector this module owns or once sent (`X-Project-Route-Actor`). */
export const PROTECTED_TENANT_HEADERS = [
    "Authorization",
    "X-Project-ID",
    "X-Project-Route-ID",
    "X-Project-Route-Actor",
    "X-Acting-Company-ID",
    "X-Company-ID",
    "X-Org-Slug",
] as const;
/** The subset of `Headers`/Axios's `AxiosHeaders` this policy needs -- both
 *  implement it natively, so each transport adapts by passing its own
 *  instance directly. */
export interface TenantHeaderCarrier {
    has(name: string): boolean;
    set(name: string, value: string): void;
    delete(name: string): void;
}
/** Transport-neutral tenant header policy shared by the Axios interceptor and
 *  the fetch wrapper: strip every protected/selector header the caller might
 *  have set, then -- unless this is a bootstrap/admission request -- inject
 *  the server-admitted ones. Security-boundary knowledge lives in exactly one
 *  place; Headers/AxiosHeaders are adapted at the call site. */
export function applyTenantHeaderPolicy(
    headers: TenantHeaderCarrier,
    admissionRequest: boolean,
): void {
    for (const key of PROTECTED_TENANT_HEADERS) headers.delete(key);
    if (!admissionRequest)
        Object.entries(getTenantRequestHeaders()).forEach(([key, value]) =>
            headers.set(key, value),
        );
}
const ROUTE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The one sanctioned per-request project selector (PP-1230): a Primepoint staff page outside every
 *  project (Project data status, `/admin/revisionDash`) acts on one row's project through that
 *  project's own URL scope, as its platform actor. Refused on a project page, so a document admitted
 *  to one project can never address another. Every selector the caller set is stripped first; the
 *  server's staff admission (`project_url_sessions`) is the authority. */
export function applyStaffProjectRoute(headers: TenantHeaderCarrier, routeId: string): void {
    if (required || closed)
        throw new TenantScopeUnavailableError("A project page cannot act on another project");
    if (!ROUTE_ID.test(routeId)) throw new Error("Invalid project route");
    for (const key of PROTECTED_TENANT_HEADERS) if (key !== "Authorization") headers.delete(key);
    headers.set("X-Project-Route-ID", routeId);
    headers.set("X-Project-Route-Actor", "platform");
}
export function captureTenantScopeBoundary() {
    const captured = generation;
    const selected = currentValidatedTenantScopeBinding();
    return {
        generation: captured,
        assertCurrent: () => {
            if (
                captured !== generation ||
                selected !== currentValidatedTenantScopeBinding() ||
                closed
            )
                throw new TenantScopeUnavailableError(
                    "Project changed while the operation was active",
                );
        },
    };
}
export function currentValidatedTenantScopeBinding(): string | null {
    return binding && !closed ? JSON.stringify(binding) : null;
}
/** Cleanup for the pre-`AbortSignal.any` fallback below, keyed by the composite
 *  signal the transport holds. Weak so a dropped signal takes its entry with
 *  it; the listeners it removes are the part that is NOT weak. */
const fallbackScopeSignalCleanups = new WeakMap<AbortSignal, () => void>();
/** One controller per scope generation, not one per request -- a bare call (the
 *  common case for polling `useQuery({ refetchInterval })`) returns it directly
 *  with nothing to track. A caller-supplied signal is combined with the scope
 *  signal via the platform's `AbortSignal.any`, which does not add a strong,
 *  module-retained reference on either source: nothing needs tracking or
 *  cleanup when the request completes normally, so a long-lived page issuing
 *  thousands of scoped requests an hour leaves no per-request state behind.
 *
 *  The fallback runs on engines without `AbortSignal.any` -- notably the whole
 *  supported iOS 15.0-17.3 range (Safari gained it in 17.4) -- and there the
 *  scope signal is long-lived while requests are not: a listener added per
 *  request and removed only on abort accumulates for every request that
 *  succeeds, because React Query (and fetch generally) never aborts the signal
 *  of a request that completed. So the fallback pairs every composite with a
 *  `release` the transport calls when the request settles, on top of removing
 *  both listeners the moment either source aborts. */
function combineWithScope(signal: AbortSignal, scopeSignal: AbortSignal): AbortSignal {
    const any = nativeAbortSignalAny();
    if (any) return any([signal, scopeSignal]);
    const controller = new AbortController();
    if (signal.aborted || scopeSignal.aborted) {
        controller.abort();
        return controller.signal;
    }
    function release() {
        signal.removeEventListener("abort", onAbort);
        scopeSignal.removeEventListener("abort", onAbort);
        fallbackScopeSignalCleanups.delete(controller.signal);
    }
    function onAbort() {
        controller.abort();
        release();
    }
    signal.addEventListener("abort", onAbort, { once: true });
    scopeSignal.addEventListener("abort", onAbort, { once: true });
    fallbackScopeSignalCleanups.set(controller.signal, release);
    return controller.signal;
}
export function scopeRequestSignal(signal?: AbortSignal | null): AbortSignal | undefined {
    if (!required) return signal ?? undefined;
    if (!scopeController) scopeController = new AbortController();
    const scopeSignal = scopeController.signal;
    if (!signal) return scopeSignal;
    return combineWithScope(signal, scopeSignal);
}
/** Drop the scope signal's hold on one settled request. Idempotent, and a
 *  no-op for the shared generation signal, a caller's own signal, and every
 *  `AbortSignal.any` composite -- none of those retain per-request state. Every
 *  transport that called `scopeRequestSignal` with a caller signal must call
 *  this once the request settles (success or failure), or the pre-composite
 *  engines grow one listener per completed request until the project changes. */
export function releaseScopeRequestSignal(signal?: AbortSignal | null): void {
    if (signal) fallbackScopeSignalCleanups.get(signal)?.();
}
/** Whether a credential renewal would be renewing a scope this Document has
 *  already been admitted to. A renewal of the live scope must not retire it:
 *  the requests that triggered the renewal captured the current generation and
 *  hold the current scope signal, so retiring it fails and aborts the very
 *  requests the renewal exists to serve. Only a Document with nothing admitted
 *  yet (first bootstrap, or after `leaveTenantScope`/`resetTenantScope`)
 *  genuinely has a scope to establish. */
export function isTenantScopeAdmitted(): boolean {
    return required && !closed && (binding !== null || selectedCompanyId !== null);
}
export function resetTenantScopeDocumentForTests(): void {
    if (process.env.NODE_ENV !== "test") throw new Error("Test-only reset");
    resetTenantScope();
    closed = false;
    required = false;
    notifyScopeKeyChanged();
}
